/**
 * Team-shared shipping-label ↔ order attaches (Firestore).
 * PDF bytes stay in Smoke Drive; this store holds page → orderKey alignment
 * so Packers + Printing Toolkit can print without re-picking pages.
 *
 * Collection: shippingLabelAttaches/{attachId}
 * Operator must paste Console rules (Hosting does not deploy Firestore rules).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './firebase';

export const SHIPPING_LABEL_ATTACHES_COLLECTION = 'shippingLabelAttaches';

export const SHIPPING_LABEL_ATTACH_STATUS = Object.freeze({
  PENDING_REVIEW: 'pending_review',
  ATTACHED: 'attached',
  DECLINED: 'declined',
  NOT_LABELS: 'not_labels',
});

const id = (prefix) =>
  `${prefix}-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`}`;

/**
 * @param {object} input
 */
export function sanitizeShippingLabelAttachPage(input = {}) {
  const pageNumber = Math.max(1, Number(input.pageNumber) || 0);
  return {
    pageNumber,
    orderKey: String(input.orderKey || '')
      .replace(/^#/, '')
      .trim(),
    status: ['matched', 'ambiguous', 'unmatched', 'manual'].includes(input.status)
      ? input.status
      : 'unmatched',
    matchSummary: String(input.matchSummary || '').slice(0, 400),
    textPreview: String(input.textPreview || '').slice(0, 200),
  };
}

/**
 * @param {object} input
 */
export function sanitizeShippingLabelAttach(input = {}) {
  const pages = Array.isArray(input.pages)
    ? input.pages.map(sanitizeShippingLabelAttachPage).filter((page) => page.pageNumber > 0)
    : [];
  const status = Object.values(SHIPPING_LABEL_ATTACH_STATUS).includes(input.status)
    ? input.status
    : SHIPPING_LABEL_ATTACH_STATUS.PENDING_REVIEW;
  return {
    id: String(input.id || '').trim() || id('attach'),
    driveItemId: input.driveItemId ? String(input.driveItemId) : null,
    fileName: String(input.fileName || 'shipping-labels.pdf').trim() || 'shipping-labels.pdf',
    pageCount: Math.max(pages.length, Number(input.pageCount) || 0),
    workingDeliveryDate: String(input.workingDeliveryDate || '').trim(),
    folderId: input.folderId ? String(input.folderId) : null,
    folderLabel: String(input.folderLabel || '').trim(),
    status,
    pages,
    attachedByUid: input.attachedByUid ? String(input.attachedByUid) : null,
    attachedByName: String(input.attachedByName || '').trim(),
    notes: String(input.notes || '').slice(0, 500),
  };
}

export function attachNeedsHumanReview(attach) {
  const pages = Array.isArray(attach?.pages) ? attach.pages : [];
  if (!pages.length) return true;
  return pages.some((page) => !String(page.orderKey || '').trim());
}

export function buildOrderKeyToPagesMap(attach) {
  /** @type {Map<string, number[]>} */
  const map = new Map();
  (attach?.pages || []).forEach((page) => {
    const orderKey = String(page.orderKey || '').trim();
    if (!orderKey) return;
    if (!map.has(orderKey)) map.set(orderKey, []);
    map.get(orderKey).push(page.pageNumber);
  });
  return map;
}

/**
 * @param {object} payload
 */
export async function saveShippingLabelAttach(payload) {
  const sanitized = sanitizeShippingLabelAttach(payload);
  if (
    sanitized.status === SHIPPING_LABEL_ATTACH_STATUS.ATTACHED &&
    attachNeedsHumanReview(sanitized)
  ) {
    throw new Error('Assign every shipping-label page to an order before saving the attach.');
  }
  const ref = doc(db, SHIPPING_LABEL_ATTACHES_COLLECTION, sanitized.id);
  const existing = await getDoc(ref);
  const body = {
    ...sanitized,
    updatedAt: serverTimestamp(),
    ...(existing.exists() ? {} : { createdAt: serverTimestamp() }),
  };
  await setDoc(ref, body, { merge: true });
  return sanitized;
}

export async function markShippingLabelAttachNotLabels({
  id: attachId,
  driveItemId,
  fileName,
  uid,
  displayName,
}) {
  return saveShippingLabelAttach({
    id: attachId || id('attach'),
    driveItemId,
    fileName,
    status: SHIPPING_LABEL_ATTACH_STATUS.NOT_LABELS,
    pages: [],
    pageCount: 0,
    attachedByUid: uid,
    attachedByName: displayName,
  });
}

export async function getShippingLabelAttach(attachId) {
  if (!attachId) return null;
  const snap = await getDoc(doc(db, SHIPPING_LABEL_ATTACHES_COLLECTION, attachId));
  if (!snap.exists()) return null;
  return sanitizeShippingLabelAttach({ id: snap.id, ...snap.data() });
}

/**
 * Latest attached docs that mention any of these order keys (client filter).
 * @param {string[]} orderKeys
 */
export async function listShippingLabelAttachesForOrderKeys(orderKeys = []) {
  const keys = new Set(
    (orderKeys || []).map((key) => String(key || '').replace(/^#/, '').trim()).filter(Boolean),
  );
  if (!keys.size) return [];
  const snap = await getDocs(
    query(
      collection(db, SHIPPING_LABEL_ATTACHES_COLLECTION),
      where('status', '==', SHIPPING_LABEL_ATTACH_STATUS.ATTACHED),
    ),
  );
  return snap.docs
    .map((entry) => sanitizeShippingLabelAttach({ id: entry.id, ...entry.data() }))
    .filter((attach) =>
      (attach.pages || []).some((page) => keys.has(String(page.orderKey || '').trim())),
    );
}

/**
 * @param {string} driveItemId
 */
export async function findShippingLabelAttachByDriveItem(driveItemId) {
  const idValue = String(driveItemId || '').trim();
  if (!idValue) return null;
  const snap = await getDocs(
    query(
      collection(db, SHIPPING_LABEL_ATTACHES_COLLECTION),
      where('driveItemId', '==', idValue),
    ),
  );
  if (snap.empty) return null;
  const toMillis = (value) => {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    const parsed = Date.parse(String(value));
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const ranked = snap.docs
    .map((entry) => ({
      attach: sanitizeShippingLabelAttach({ id: entry.id, ...entry.data() }),
      sort: toMillis(entry.data()?.updatedAt || entry.data()?.createdAt),
    }))
    .sort((left, right) => right.sort - left.sort);
  return ranked[0]?.attach || null;
}

/**
 * Live attached map for Pack / Printing: orderKey → attach pages.
 * @param {(attaches: object[]) => void} onChange
 */
export function subscribeAttachedShippingLabels(onChange) {
  const q = query(
    collection(db, SHIPPING_LABEL_ATTACHES_COLLECTION),
    where('status', '==', SHIPPING_LABEL_ATTACH_STATUS.ATTACHED),
  );
  return onSnapshot(
    q,
    (snap) => {
      const attaches = snap.docs.map((entry) =>
        sanitizeShippingLabelAttach({ id: entry.id, ...entry.data() }),
      );
      onChange(attaches);
    },
    () => onChange([]),
  );
}

/**
 * Live attaches for desk chips: ATTACHED + PENDING_REVIEW (needs-check).
 * Wave assemble still uses ATTACHED-only via subscribeAttachedShippingLabels.
 * @param {(attaches: object[]) => void} onChange
 */
export function subscribeDeskShippingLabelAttaches(onChange) {
  let attached = [];
  let pending = [];
  const emit = () => onChange([...attached, ...pending]);

  const unsubAttached = onSnapshot(
    query(
      collection(db, SHIPPING_LABEL_ATTACHES_COLLECTION),
      where('status', '==', SHIPPING_LABEL_ATTACH_STATUS.ATTACHED),
    ),
    (snap) => {
      attached = snap.docs.map((entry) =>
        sanitizeShippingLabelAttach({ id: entry.id, ...entry.data() }),
      );
      emit();
    },
    () => {
      attached = [];
      emit();
    },
  );

  const unsubPending = onSnapshot(
    query(
      collection(db, SHIPPING_LABEL_ATTACHES_COLLECTION),
      where('status', '==', SHIPPING_LABEL_ATTACH_STATUS.PENDING_REVIEW),
    ),
    (snap) => {
      pending = snap.docs.map((entry) =>
        sanitizeShippingLabelAttach({ id: entry.id, ...entry.data() }),
      );
      emit();
    },
    () => {
      pending = [];
      emit();
    },
  );

  return () => {
    unsubAttached();
    unsubPending();
  };
}

export async function updateShippingLabelAttachPages(attachId, pages, extra = {}) {
  const sanitizedPages = (pages || []).map(sanitizeShippingLabelAttachPage);
  const needsReview = sanitizedPages.some((page) => !page.orderKey);
  await updateDoc(doc(db, SHIPPING_LABEL_ATTACHES_COLLECTION, attachId), {
    pages: sanitizedPages,
    pageCount: sanitizedPages.length,
    status: needsReview
      ? SHIPPING_LABEL_ATTACH_STATUS.PENDING_REVIEW
      : SHIPPING_LABEL_ATTACH_STATUS.ATTACHED,
    ...extra,
    updatedAt: serverTimestamp(),
  });
}

